import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { DNF_CHANTIERS_PATH } from './LandingPage';
import PoweredByBadge from '../components/PoweredByBadge';
import FollowButton from '../components/FollowButton';
import { useTenant } from '../context/TenantContext';
import { MapPin, Calendar, Clock, User, Camera, ChevronLeft, ChevronRight, X, Building2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE = API_URL.replace('/api', '');
// Valeurs par défaut historiques DNF (utilisées lorsque le tenant est DNF).
const BRAND = '#8B4513';
const CREAM = '#F4F0E8';


const PARTNER_TYPE_LABELS = {
  SYNDIC:           'Syndic / Maître d\'ouvrage',
  MAITRE_OEUVRE:    'Maître d\'œuvre',
  BUREAU_CONTROLE:  'Bureau de contrôle',
  COORDINATEUR_SPS: 'Coordinateur SPS',
  ENTREPRISE:       'Entreprises',
  OTHER:            'Autres intervenants',
};
const TYPE_ORDER = ['SYNDIC', 'MAITRE_OEUVRE', 'BUREAU_CONTROLE', 'COORDINATEUR_SPS', 'ENTREPRISE', 'OTHER'];

function PageShell({ children, poweredBy, followSlug }) {
  const { tenant, isLegacyDnfHost } = useTenant();
  const { companySlug } = useParams();
  // DNF historique : rendu figé à l'identique (logo, slogan, marron, crème).
  // Autres tenants : logo, nom, slogan et couleur issus de leurs réglages.
  const isDnf = isLegacyDnfHost || tenant?.slug === 'dnf-sas';
  // Où ramènent le logo et « Retour aux chantiers » :
  // - DNF (vitrine historique sur /) : accueil DNF, directement sur l'onglet chantiers ;
  // - page tenant /e/{slug}/chantier/... : vitrine de l'entreprise /e/{slug} ;
  // - URL historique /chantier/{slug} sans tenant : racine du site.
  const homeLink = isDnf ? '/' : (companySlug ? `/e/${companySlug}` : '/');
  const backLink = isDnf ? DNF_CHANTIERS_PATH : homeLink;
  const brand = isDnf ? BRAND : (tenant?.color || '#334155');
  const cream = isDnf ? CREAM : '#f8fafc';
  const pageBg = isDnf ? '#F4F0E8' : (tenant?.pageBgColor || '#f8fafc');
  const logoSrc = isDnf ? '/logo.png' : (tenant?.logo ? `${API_BASE}/uploads/${tenant.logo}` : null);
  const displayName = isDnf ? 'DNF sas' : (tenant?.name || '');
  const tagline = isDnf ? 'Ravalement · Pierre de taille · Peinture' : (tenant?.tagline || '');

  return (
    <div style={{ minHeight: '100vh', background: pageBg, fontFamily: 'Arial, sans-serif' }}>
      <header style={{ background: pageBg, borderBottom: '1px solid rgba(0,0,0,0.08)', padding: '20px 16px', textAlign: 'center' }}>
        <Link to={homeLink} style={{ textDecoration: 'none' }}>
          {logoSrc ? (
            <img src={logoSrc} alt={isDnf ? 'DNF' : displayName}
              style={{ height: 72, width: 'auto', marginBottom: 10, ...(isDnf ? { mixBlendMode: 'multiply' } : {}) }} />
          ) : (
            <h2 style={{ margin: '0 0 10px', color: brand, fontWeight: 800 }}>{displayName}</h2>
          )}
        </Link>
        {tagline && (
          <p style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '3px', color: brand, textTransform: 'uppercase', margin: 0, fontFamily: 'Arial, sans-serif' }}>
            {tagline}
          </p>
        )}
        <div style={{ marginTop: 12 }}>
          <Link to={backLink} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            color: brand, fontSize: '0.82rem', textDecoration: 'none', fontWeight: 600, opacity: 0.8,
          }}>
            <ArrowLeft size={13} /> Retour aux chantiers
          </Link>
        </div>
      </header>
      {children}
      {followSlug && <FollowButton projectSlug={followSlug} brand={brand} />}
      <footer style={{ background: brand, color: cream, padding: '18px 16px', textAlign: 'center' }}>
        <p style={{ fontSize: '0.8rem', opacity: 0.8, margin: 0, letterSpacing: '0.5px' }}>
          © {new Date().getFullYear()} {displayName} — Tous droits réservés
        </p>
      </footer>
      <PoweredByBadge show={poweredBy} />
    </div>
  );
}

export default function PublicProject() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Le QR code encode ?source=qr : c'est le navigateur qui le reçoit, il faut
  // donc le repasser à l'API, seule à compter les visites.
  const sourceQuery = searchParams.get('source') === 'qr' ? '?source=qr' : '';
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  // La visionneuse ouverte est une entrée d'historique (state.lightbox) : le
  // bouton « précédent » du navigateur (ou le geste de retour sur mobile) la
  // referme et laisse l'utilisateur sur le chantier, au lieu de quitter la page.
  const lightboxOpen = !!location.state?.lightbox && !!selectedPhoto;
  const openLightbox = useCallback((photo, idx) => {
    setSelectedPhoto(photo);
    setPhotoIndex(idx);
    navigate(location.pathname + location.search, { state: { lightbox: true } });
  }, [navigate, location.pathname, location.search]);
  const closeLightbox = useCallback(() => {
    if (location.state?.lightbox) navigate(-1);
    else setSelectedPhoto(null);
  }, [navigate, location.state]);

  useEffect(() => {
    fetch(`${API_URL}/public/chantier/${slug}${sourceQuery}`)
      .then(r => {
        if (r.status === 403) throw new Error('disabled');
        if (!r.ok) throw new Error('notfound');
        return r.json();
      })
      .then(setProject)
      .catch(e => setError(e.message === 'disabled' ? 'disabled' : 'notfound'));
  }, [slug, sourceQuery]);

  const navigatePhoto = useCallback((dir) => {
    if (!project?.photos) return;
    setPhotoIndex(idx => {
      const newIdx = idx + dir;
      if (newIdx >= 0 && newIdx < project.photos.length) {
        setSelectedPhoto(project.photos[newIdx]);
        return newIdx;
      }
      return idx;
    });
  }, [project]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigatePhoto(1);
      if (e.key === 'ArrowLeft') navigatePhoto(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, closeLightbox, navigatePhoto]);

  if (error) {
    return (
      <PageShell poweredBy={project?.poweredBy} followSlug={project?.followEnabled ? project.slug : null}>
        <div style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>{error === 'disabled' ? '🔒' : '🔍'}</div>
          <h2 style={{ color: '#1e293b', marginBottom: 8 }}>{error === 'disabled' ? 'Accès désactivé' : 'Chantier introuvable'}</h2>
          <p style={{ color: '#64748b' }}>
            {error === 'disabled'
              ? "L'accès public à ce chantier a été désactivé."
              : "Ce lien n'est plus valide ou le chantier n'existe pas."}
          </p>
        </div>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <PageShell poweredBy={project?.poweredBy} followSlug={project?.followEnabled ? project.slug : null}>
        <div style={{ textAlign: 'center', padding: '80px 16px', color: '#94a3b8' }}>
          Chargement du chantier...
        </div>
      </PageShell>
    );
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const hasPhotos = project.photos && project.photos.length > 0;

  const partnersByType = TYPE_ORDER.reduce((acc, type) => {
    const items = (project.partners || []).filter(pp => pp.partner.type === type);
    if (items.length > 0) acc.push({ type, label: PARTNER_TYPE_LABELS[type], items });
    return acc;
  }, []);

  return (
    <PageShell poweredBy={project?.poweredBy} followSlug={project?.followEnabled ? project.slug : null}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px 48px' }}>

        {/* ── PANNEAU DE CHANTIER (document) ── */}
        <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.08)', marginBottom: 28, overflow: 'hidden', border: '1px solid #e8ddd4' }}>

          {/* En-tête : titre + statut */}
          <div style={{ borderTop: `4px solid ${BRAND}`, padding: '20px 24px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
              <div>
                {project.residenceName && (
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', fontFamily: 'Georgia, serif' }}>{project.residenceName}</div>
                )}
                <h1 style={{ margin: 0, fontSize: project.residenceName ? '0.95rem' : '1.4rem', fontWeight: project.residenceName ? 500 : 700, color: project.residenceName ? '#555' : '#1e293b', fontFamily: 'Georgia, serif' }}>
                  {project.name}
                </h1>
              </div>
            </div>
            {(project.duration || project.dpNumber || project.dpDate) && (
              <div style={{ marginTop: 10, fontSize: '0.82rem', color: '#64748b', lineHeight: 1.8 }}>
                {project.dpNumber && <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b', letterSpacing: '0.3px' }}>{project.dpNumber}</div>}
                {project.duration && <div>Durée des travaux : <strong>{project.duration}</strong></div>}
                {project.dpDate && <div>Délivrée le {project.dpDate}</div>}
              </div>
            )}
          </div>

          {/* Sections intervenants */}
          {partnersByType.map(group => (
            <div key={group.type} style={{ borderTop: '1px solid #f0ebe4', padding: '14px 24px' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, borderBottom: `2px solid ${BRAND}`, paddingBottom: 6 }}>
                {group.label}
              </div>
              {group.items.map(pp => (
                <div key={pp.id} style={{ display: 'grid', gridTemplateColumns: '72px 1fr auto', gap: 14, alignItems: 'center', padding: '8px 0', borderBottom: '1px dashed #f0ebe4' }}>
                  {pp.partner.logo ? (
                    <img src={`${API_BASE}/uploads/${pp.partner.logo}`} alt={pp.partner.name}
                      style={{ width: 64, height: 48, objectFit: 'contain', borderRadius: 6, background: '#f8f9fa', padding: 4, border: '1px solid #e8ddd4' }} />
                  ) : (
                    <div style={{ width: 64, height: 48, borderRadius: 6, background: '#f8f9fa', border: '1px solid #e8ddd4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Building2 size={18} style={{ color: '#ccc' }} />
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>{pp.partner.name}</div>
                    {pp.partner.contactName && (
                      <div style={{ fontSize: '0.78rem', color: '#555' }}>
                        {pp.partner.contactName}{pp.partner.contactTitle && <em> {pp.partner.contactTitle}</em>}
                      </div>
                    )}
                    {pp.lotLabel && <div style={{ fontSize: '0.78rem', color: BRAND, fontWeight: 600 }}>{pp.lotLabel}</div>}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.78rem', color: '#555' }}>
                    {pp.partner.address && <div style={{ lineHeight: 1.4 }}>{pp.partner.address}</div>}
                    {pp.partner.phone && <div style={{ marginTop: 2 }}>Tél : {pp.partner.phone}</div>}
                  </div>
                </div>
              ))}
            </div>
          ))}


          {/* Dates + adresse */}
          {(project.address || project.clientName || project.startDate || project.endDate) && (
            <div style={{ borderTop: '1px solid #f0ebe4', padding: '14px 24px', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {project.address && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <MapPin size={14} color={BRAND} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.83rem', color: '#333' }}>{project.address}</span>
                </div>
              )}
              {project.clientName && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <User size={14} color={BRAND} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.83rem', color: '#333' }}>{project.clientName}</span>
                </div>
              )}
              {project.startDate && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Calendar size={14} color={BRAND} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.83rem', color: '#333' }}>Début : <strong>{fmtDate(project.startDate)}</strong></span>
                </div>
              )}
              {project.endDate && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Clock size={14} color={BRAND} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.83rem', color: '#333' }}>Fin prévue : <strong>{fmtDate(project.endDate)}</strong></span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── PHOTOS ── */}
        {hasPhotos && (
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Camera size={14} /> Photos du chantier
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 400, letterSpacing: 0 }}>({project.photos.length})</span>
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {project.photos.map((photo, idx) => (
                <div key={photo.id} onClick={() => openLightbox(photo, idx)}
                  style={{ borderRadius: 8, overflow: 'hidden', cursor: 'pointer', position: 'relative', aspectRatio: '4/3', background: '#f1f5f9' }}>
                  <img src={`${API_BASE}/uploads/${photo.filename}`} alt={photo.caption || 'Photo'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.2s' }}
                    onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
                    onMouseLeave={e => e.target.style.transform = ''} />
                  {photo.caption && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.75rem', padding: '4px 8px' }}>
                      {photo.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div onClick={closeLightbox} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={closeLightbox} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
            <X size={20} />
          </button>
          {photoIndex > 0 && (
            <button onClick={e => { e.stopPropagation(); navigatePhoto(-1); }} style={{ position: 'absolute', left: 12, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
              <ChevronLeft size={24} />
            </button>
          )}
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', textAlign: 'center' }}>
            <img src={`${API_BASE}/uploads/${selectedPhoto.filename}`} alt={selectedPhoto.caption || 'Photo'} style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: 8 }} />
            {selectedPhoto.caption && <p style={{ color: '#fff', marginTop: 10, fontSize: '0.9rem', opacity: 0.85 }}>{selectedPhoto.caption}</p>}
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', marginTop: 6 }}>{photoIndex + 1} / {project.photos.length}</p>
          </div>
          {photoIndex < project.photos.length - 1 && (
            <button onClick={e => { e.stopPropagation(); navigatePhoto(1); }} style={{ position: 'absolute', right: 12, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      )}
    </PageShell>
  );
}
